import { components, paths } from "@/api-schema";
import {
  PathsV1DepartmentSearchPostParametersQueryOrderByDirection,
  PathsV1DepartmentSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useDepartmentDeleteMutation } from "@/common/api/department/use-department-delete.mutation";
import { useDepartmentRestoreMutation } from "@/common/api/department/use-department-restore.mutation";
import { useDepartmentSearchQuery } from "@/common/api/department/use-department-search.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import { DeletedStatus, DeletedStatusFilter } from "@/components/filter/predefined/deleted-status-filter";
import i18next from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { departmentTableDefaultColumns } from "./department-table-columns";

type Department = components["schemas"]["Department"] & RowWithIdentifier;
type DepartmentSorting = NonNullable<paths["/v1/department/search"]["post"]["parameters"]["query"]["orderBy"]>;
type DepartmentQueryParams = paths["/v1/department/search"]["post"]["parameters"]["query"];
type DepartmentRequestBody = paths["/v1/department/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [departmentTableDefaultColumns.columnName, departmentTableDefaultColumns.columnDeletedAt];

const DEPARTMENT_TABLE_CONFIG: PredefinedTableConfig<DepartmentSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1DepartmentSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1DepartmentSearchPostParametersQueryOrderByField.Name,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("common:SearchDepartments"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: false,
  } as const,
  sortingMapping: {
    name: ["name"],
    deletedAt: ["deletedAt"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
} as const;

const DEPARTMENT_TABLE_QUERY_HOOKS: DataFetchHooks<Department, DepartmentQueryParams, DepartmentRequestBody> = {
  useSearchQuery: useDepartmentSearchQuery,
  useDeleteMutation: useDepartmentDeleteMutation,
  useRestoreMutation: useDepartmentRestoreMutation,
} as const;

export interface DepartmentTableProps extends BasePredefinedTableProps<
  Department,
  DepartmentSorting,
  DepartmentQueryParams,
  DepartmentRequestBody
> {}

const BaseDepartmentTable = createBasePredefinedTable({
  config: DEPARTMENT_TABLE_CONFIG,
  queryHooks: DEPARTMENT_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function DepartmentTable(props: DepartmentTableProps) {
  const { t } = useTranslation(["common"]);
  const {
    name = "department-table",
    showCreateButton = true,
    body = { deletedAt: [DeletedStatus.ACTIVE, DeletedStatus.DELETED] },
    filters,
    filterValues,
    columns,
    ...restProps
  } = props;

  const defaultFilters = useMemo(() => filters ?? [new DeletedStatusFilter()], [filters]);
  const defaultFilterValues = useMemo(() => filterValues ?? { deletedAt: [DeletedStatus.ACTIVE] }, [filterValues]);

  return (
    <BaseDepartmentTable
      searchPlaceholder={t("common:SearchDepartments")}
      {...{
        name,
        showCreateButton,
        body,
        columns,
        filters: defaultFilters,
        filterValues: defaultFilterValues,
        confirmActions: ["delete", "restore"],
        toastMessages: {
          deleteSuccess: t("DepartmentHasBeenDeleted"),
          deleteError: t("DeleteDepartmentError"),
          restoreSuccess: t("DepartmentHasBeenRestored"),
          restoreError: t("RestoreDepartmentError"),
        },
        confirmLabels: {
          delete: (row: Department) => ({
            title: row.deletedAt ? t("DeleteDepartmentPermanently") : t("DeleteDepartment"),
            text: row.deletedAt ? t("DeleteDepartmentPermanentlyDescription") : t("DeleteDepartmentDescription"),
            confirmButton: row.deletedAt ? t("YesDeletePermanently") : t("ram:YesDelete"),
            cancelButton: t("Cancel"),
          }),
          restore: {
            title: t("RestoreDepartment"),
            text: t("RestoreDepartmentDescription"),
            confirmButton: t("YesRestore"),
            cancelButton: t("Cancel"),
          },
        },
        ...restProps,
      }}
    />
  );
}
